import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Hidden from "@material-ui/core/Hidden";
import Favorite from "@material-ui/icons/Favorite";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import IconButton from "@material-ui/core/IconButton";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";

import Menu from "@material-ui/icons/Menu";
import MenuOpen from "@material-ui/icons/MenuOpen";

import AdminNavbarLinks from "./AdminNavbarLinks";
import Button from "components/CustomButtons/Button.js";
import CustomSearch from "./CustomSearchField";

import logoWhite from "assets/img/logo-white.png";

import { SearchProvider, useSearch } from "../../../src/context/SearchContext";

import { useLocation } from "react-router-dom"; // ✅ added

import styles from "assets/jss/material-dashboard-pro-react/components/adminNavbarStyle.js";

const useStyles = makeStyles(styles);

export default function AdminNavbar(props) {
  const classes = useStyles();
  const { color, rtlActive, brandText } = props;
  const { searchQuery, setSearchQuery } = useSearch();
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const location = useLocation(); // ✅ added

  const appBarClasses = cx({
    [" " + classes[color]]: color,
  });

  const sidebarMinimize =
    classes.sidebarMinimize +
    " " +
    cx({
      [classes.sidebarMinimizeRTL]: rtlActive,
    });

  // debounce (unchanged)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 500);
    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  // ✅ clear search when URL changes
  useEffect(() => {
    setSearchQuery("");
    setDebouncedQuery("");
    setMobileSearchOpen(false);
  }, [location.pathname]); // reacts to route change

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchIconClick = () => {
    setMobileSearchOpen(true);
  };

  const handleCloseMobileSearch = () => {
    setMobileSearchOpen(false);
  };

  return (
    <SearchProvider>
      <AppBar className={classes.appBar + appBarClasses}>
        <Toolbar className={classes.container}>
          <Hidden smDown implementation="css">
            <div className={sidebarMinimize}>
              <IconButton
                className={classes.sidebarToggle}
                onClick={props.sidebarMinimize}
                aria-label={props.miniActive ? "Expand sidebar" : "Collapse sidebar"}
                title={props.miniActive ? "Expand sidebar" : "Collapse sidebar"}
              >
                {props.miniActive ? <Menu /> : <MenuOpen />}
              </IconButton>
            </div>
          </Hidden>

          <div className={classes.flex}>
            <Button href="#" className={classes.title} color="transparent">
              <img src={logoWhite} alt="Logo" className={classes.brandLogo} />
              {brandText}
            </Button>
          </div>

          {/* Large screen search */}
          <Hidden xsDown implementation="css">
            <div style={{ flexGrow: 1, marginLeft: "20px" }}>
              <CustomSearch
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Use nickname..."
              />
            </div>
          </Hidden>

          {/* Small screen search toggle */}
          <Hidden smUp implementation="css">
            {mobileSearchOpen ? (
              <ClickAwayListener onClickAway={handleCloseMobileSearch}>
                <div
                  style={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 64,
                    backgroundColor: "white",
                    padding: "8px 16px",
                    zIndex: 1100,
                  }}
                >
                  <TextField
                    autoFocus
                    fullWidth
                    variant="outlined"
                    size="small"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search by nickname"
                  />
                </div>
              </ClickAwayListener>
            ) : (
              <IconButton color="inherit" onClick={handleSearchIconClick}>
                <SearchIcon />
              </IconButton>
            )}
          </Hidden>

          {/* Right-side icons */}
          <Hidden smDown implementation="css">
            <AdminNavbarLinks rtlActive={rtlActive} />
          </Hidden>
          <Hidden mdUp implementation="css">
            <Button
              className={classes.appResponsive}
              color="transparent"
              justIcon
              aria-label="open drawer"
              onClick={props.handleDrawerToggle}
            >
              <Menu />
            </Button>
          </Hidden>
        </Toolbar>
      </AppBar>
    </SearchProvider>
  );
}

AdminNavbar.propTypes = {
  color: PropTypes.oneOf(["primary", "info", "success", "warning", "danger"]),
  rtlActive: PropTypes.bool,
  brandText: PropTypes.string,
  miniActive: PropTypes.bool,
  handleDrawerToggle: PropTypes.func,
  sidebarMinimize: PropTypes.func,
};
